import React from 'react';
import { createPortal } from 'react-dom';
import { Spacer } from '../Spacer';

type ButtonStyle = 'submit' | 'cancel' | 'warning';

type ButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'button'
> & {
  text: string;
  buttonStyle: ButtonStyle;
};

export const PortalModal: React.FC<
  React.PropsWithChildren & {
    title: string;
    primaryButton: ButtonProps;
    secondaryButton?: ButtonProps;
  }
> = ({ title, children, primaryButton, secondaryButton = null }) => {
  const [mounted, setMounted] = React.useState<boolean>(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  return mounted
    ? createPortal(
        <React.Fragment>
          <div className="tw-fixed tw-inset-0 tw-bg-main-80" />
          <div className="tw-fixed tw-inset-0 tw-grid tw-grid-cols-1 tw-place-items-center tw-justify-center">
            <div className="tw-w-[calc(100%-2rem)] tw-max-w-[28rem] tw-rounded-4 tw-bg-base-lv1">
              <div className="tw-border-b tw-border-b-main-10 tw-p-[1rem] tw-text-h4 tw-text-main-100">
                {title}
              </div>
              <Spacer level={1.5} />
              <div className="tw-px-[1rem] tw-text-base1">{children}</div>
              <Spacer level={2} />
              <div className="tw-px-[1rem] tw-pb-[1rem]">
                <div className="tw-flex tw-justify-end tw-gap-[0.5rem]">
                  {[secondaryButton, primaryButton].map((props, index) =>
                    props ? <Button key={index} {...props} /> : null
                  )}
                </div>
              </div>
            </div>
          </div>
        </React.Fragment>,
        document.getElementById('modal') as Element
      )
    : null;
};

const Button: React.FC<ButtonProps> = ({ text, buttonStyle, ...props }) => (
  <button
    {...props}
    type="button"
    className={
      buttonStyle === 'warning'
        ? 'tw-block tw-w-fit tw-min-w-[5rem] tw-appearance-none tw-rounded-8 tw-border-none tw-bg-caution-lv1 tw-p-[0.75rem] tw-text-button tw-text-base-lv1'
        : buttonStyle === 'cancel'
          ? 'tw-block tw-w-fit tw-min-w-[5rem] tw-appearance-none tw-rounded-8 tw-border-none tw-bg-main-40 tw-p-[0.75rem] tw-text-button tw-text-base-lv1'
          : 'tw-block tw-w-fit tw-min-w-[5rem] tw-appearance-none tw-rounded-8 tw-border-none tw-bg-brand-lv1 tw-p-[0.75rem] tw-text-button tw-text-base-lv1'
    }
  >
    {text}
  </button>
);
